// SEAT_LAYOUT — 4 家手牌 / 副露版面定義 + 對譯成 CHandView config 的工廠
// 從 game-scene.ts 抽出，目的：把「玩家視角座標 ↔ 螢幕方向座標」這層翻譯層
// 從 Scene 內挪出，讓 game-scene 專心當 Core ↔ UI 協調者。
//
// === 使用圖片 ===
// 無 — 本檔僅為座標 / 配置宣告 + 對譯邏輯，實際渲染由 CHandView 與其內部 CTileSprite 處理

import type Phaser from 'phaser';

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '@config/game-config';
import type { TTile } from '@core/tile';
import type { TSeat } from '@core/types';
import {
  CHandView,
  FlowerGrowDirection,
  type TFlowerGrowDirection,
  MeldDirection,
  MeldKongStackDirection,
} from '@ui/index';

// ============================================================================
// SEAT_LAYOUT — 4 家手牌 / 副露版面定義
// ============================================================================
// 設計原則：每家以「面對牌桌的玩家視角」為基準描述座標。
//   - 手牌「最左邊」對齊玩家視角左 → 該家面對牌桌時的左手方
//   - 副露「最右邊」對齊玩家視角右 → 該家面對牌桌時的右手方
//
// 視角 vs 螢幕方向對照：
//   - 自家（bottom）：視角左 = 螢幕左、視角右 = 螢幕右
//   - 右家（right） ：視角左 = 螢幕下、視角右 = 螢幕上
//   - 對家（top）   ：視角左 = 螢幕右、視角右 = 螢幕左
//   - 左家（left）  ：視角左 = 螢幕上、視角右 = 螢幕下
//
// 各家欄位名稱依視角自然投射到螢幕方向命名（同個語意四家不一樣，便於閱讀）：
//   - 自家：手牌「最左邊緣 X」（handLeftEdgeX）+ 副露「最右邊緣距畫面右緣」（meldDistanceFromScreenRight）
//   - 右家：手牌「最下邊緣距畫面底」（handDistanceFromScreenBottom）+ 副露「最上邊緣 Y」（meldTopEdgeY）
//   - 對家：手牌「最右邊緣距畫面右緣」（handDistanceFromScreenRight）+ 副露「最左邊緣 X」（meldLeftEdgeX）
//   - 左家：手牌「最上邊緣 Y」（handTopEdgeY）+ 副露「最下邊緣距畫面底」（meldDistanceFromScreenBottom）

// 共用欄位（meld 區 + 視覺除錯 — 四家都有，差異只在 anchor 與 kong 方向）
type TSeatLayoutMeldCommon = {
  readonly meldTileGap: number; // 副露之間間距（px，未經 scale）
  readonly meldScale: number; // 副露縮放
  readonly meldTileWidth: number; // 單張副露牌實際視覺寬（at scale=1，給 edge anchor / kong 凸字位移用）
  readonly meldTileHeight: number; // 單張副露牌實際視覺高（at scale=1）
  readonly meldGroupGap: number; // 相鄰兩組副露之間間距（px，未經 scale；與 meldTileGap 同單位）
  readonly meldKongStackDirection: 'up' | 'down' | 'left' | 'right'; // 凸字方向
  readonly meldKongStackOffset?: number; // 凸字位移（post-scale px）；不設 = 整張牌 flush 疊
  /**
   * 整家牌（手牌 + 副露）的透明度，0~1（0 = 透明、1 = 不透明）。
   * 必填；上線一律設 1，開發時可調低（例 0.5）看到底層 / 旁邊重疊區域方便對齊微調。
   */
  readonly alpha: number;
  // ---- 花牌區（補花後公開展示；四家都有，origin + direction 簡易排版）----
  readonly flowerOriginX: number; // 花牌第一張中心 X
  readonly flowerOriginY: number; // 花牌第一張中心 Y
  readonly flowerTileGap: number; // 花牌間距（px，未經 flowerScale）
  readonly flowerScale: number; // 花牌縮放
  readonly flowerGrowDirection: TFlowerGrowDirection; // 花牌生長方向（第一張＝視角左，往視角右延伸的螢幕方向）
  readonly flowerFirstOnTop?: boolean; // 直排重疊家：第一張壓最上、依序遞減（不設＝後者蓋前者）
};

type TBottomSeatLayout = {
  readonly position: 'bottom';
  // 手牌：玩家視角左 = 螢幕左 → 「最左邊緣」直接對齊
  readonly handLeftEdgeX: number; // 暗牌「最左邊緣」X（距畫面左緣）
  readonly handCenterY: number; // 暗牌中心 Y（手牌排在這條 Y 線上）
  readonly handTileGap: number; // 暗牌間距（px，未經 scale）
  readonly handScale: number;
  readonly handTileWidth?: number; // 暗牌實際視覺寬（給 edge anchor 算半寬用；預設 TILE_BODY_WIDTH=90）
  // 副露：玩家視角右 = 螢幕右 → 「最右邊緣」距螢幕右緣
  readonly meldDistanceFromScreenRight: number; // 副露「最右邊緣」距畫面右緣
  readonly meldCenterY: number; // 副露中心 Y
} & TSeatLayoutMeldCommon;

type TRightSeatLayout = {
  readonly position: 'right';
  // 手牌：玩家視角左 = 螢幕下 → 「最下邊緣」距螢幕底
  readonly handDistanceFromScreenBottom: number; // 暗牌「最下邊緣」距畫面底
  readonly handCenterX: number; // 暗牌中心 X
  readonly handTileGap: number;
  readonly handScale: number;
  readonly handTileHeight?: number; // 暗牌實際視覺高（給 edge anchor 算半高用；預設 TILE_BODY_HEIGHT=144）
  // 副露：玩家視角右 = 螢幕上 → 「最上邊緣」直接對齊
  readonly meldTopEdgeY: number; // 副露「最上邊緣」Y
  readonly meldCenterX: number; // 副露中心 X
} & TSeatLayoutMeldCommon;

type TTopSeatLayout = {
  readonly position: 'top';
  // 手牌：玩家視角左 = 螢幕右 → 「最右邊緣」距螢幕右緣
  readonly handDistanceFromScreenRight: number; // 暗牌「最右邊緣」距畫面右緣
  readonly handCenterY: number;
  readonly handTileGap: number;
  readonly handScale: number;
  readonly handTileWidth?: number; // 預設 TILE_BODY_WIDTH=90；hand-top 用 tile-back-up 實際視覺寬 63
  // 副露：玩家視角右 = 螢幕左 → 「最左邊緣」直接對齊
  readonly meldLeftEdgeX: number; // 副露「最左邊緣」X
  readonly meldCenterY: number;
} & TSeatLayoutMeldCommon;

type TLeftSeatLayout = {
  readonly position: 'left';
  // 手牌：玩家視角左 = 螢幕上 → 「最上邊緣」直接對齊
  readonly handTopEdgeY: number; // 暗牌「最上邊緣」Y
  readonly handCenterX: number;
  readonly handTileGap: number;
  readonly handScale: number;
  readonly handTileHeight?: number;
  // 副露：玩家視角右 = 螢幕下 → 「最下邊緣」距螢幕底
  readonly meldDistanceFromScreenBottom: number; // 副露「最下邊緣」距畫面底
  readonly meldCenterX: number;
} & TSeatLayoutMeldCommon;

export type TSeatLayout = TBottomSeatLayout | TRightSeatLayout | TTopSeatLayout | TLeftSeatLayout;

export const SEAT_LAYOUT: { readonly [K in TSeat]: TSeatLayout } = {
  // 自家（bottom）：面朝上看螢幕。視角左/右 = 螢幕左/右
  0: {
    position: 'bottom',
    handLeftEdgeX: 25, // 手牌「最左邊緣」距畫面左緣（玩家視角左 = 螢幕左）
    handCenterY: 650, // 手牌中心 Y（接近螢幕底）
    handTileGap: 90, // 暗牌間距
    handScale: 0.68,
    meldDistanceFromScreenRight: 100, // 副露「最右邊緣」距畫面右緣（玩家視角右 = 螢幕右）
    meldCenterY: 660, // 副露中心 Y（跟手牌同一列）
    meldTileGap: 60,
    meldScale: 0.8, // 自家副露不縮，較顯眼
    meldTileWidth: 63, // tile-front-down 原始寬（自家副露用 discard-front kind）
    meldTileHeight: 99, // tile-front-down 原始高
    meldGroupGap: 20, // 兩組副露之間間距
    meldKongStackDirection: MeldKongStackDirection.Up, // 凸字朝玩家
    meldKongStackOffset: 26, // 凸字小幅突出（不疊整張，視覺較緊湊）
    alpha: 1, // 上線值 1；微調對齊時可調 0.5 看重疊
    // 花牌：比照露牌大小（meldScale 0.8）、橫排；手牌「左前方」（視角左=螢幕左、前=螢幕上）
    // 左下角被「左家手牌(直排底端~Y480)＋剩牌計數(X10~280,Y500~600)」夾住 →
    // 唯一不重疊又貼手牌的位置：計數器右側、手牌左段正上方
    flowerOriginX: 315,
    flowerOriginY: 550,
    flowerTileGap: 60, // 同 meldTileGap
    flowerScale: 0.8, // 同 meldScale
    flowerGrowDirection: FlowerGrowDirection.Right, // 第一張在視角左＝螢幕左，往螢幕右排
  },
  // 右家（right）：坐在螢幕右側、面朝左。視角左 = 螢幕下、視角右 = 螢幕上
  1: {
    position: 'right',
    handDistanceFromScreenBottom: 12, // 手牌「最下邊緣」距畫面底（玩家視角左 = 螢幕下）
    handCenterX: 1230, // 手牌中心 X（接近螢幕右）
    handTileGap: 38,
    handScale: 0.8,
    handTileHeight: 99, // tile-side-up 實際視覺高（hand-right 單層側面 63×99）
    meldTopEdgeY: 130, // 副露「最上邊緣」Y（玩家視角右 = 螢幕上）
    meldCenterX: 1220, // 副露中心 X（手牌左側一點）
    meldTileGap: 40,
    meldScale: 0.8,
    meldTileWidth: 90, // tile-side-down 實際寬（meld-right 橫躺）
    meldTileHeight: 63, // tile-side-down 實際高
    meldGroupGap: 40,
    meldKongStackDirection: MeldKongStackDirection.Up, // 凸字朝玩家
    meldKongStackOffset: 20, // 凸字小幅突出（不疊整張，視覺較緊湊）
    alpha: 1, // 上線值 1；微調對齊時可調 0.5 看重疊
    // 花牌：比照露牌大小（meldScale 0.8）、直排；手牌「左前方」（視角左=螢幕下、前=螢幕左）
    flowerOriginX: 1120,
    flowerOriginY: 560,
    flowerTileGap: 40, // 同 meldTileGap
    flowerScale: 0.8, // 同 meldScale
    flowerGrowDirection: FlowerGrowDirection.Up, // 第一張在視角左＝螢幕下，往螢幕上排
    flowerFirstOnTop: true, // 直排重疊：第一張（最下）壓住上面後續花牌
  },
  // 對家（top）：坐在螢幕上方、面朝下。視角左 = 螢幕右、視角右 = 螢幕左
  2: {
    position: 'top',
    handDistanceFromScreenRight: 26, // 手牌「最右邊緣」距畫面右緣（玩家視角左 = 螢幕右）
    handCenterY: 60, // 手牌中心 Y（接近螢幕頂）
    handTileGap: 62,
    handScale: 0.9,
    handTileWidth: 63, // tile-back-up 實際寬（hand-top 單層牌背 63×99）
    meldLeftEdgeX: 120, // 副露「最左邊緣」X（玩家視角右 = 螢幕左）
    meldCenterY: 80,
    meldTileGap: 90,
    meldScale: 0.5,
    meldTileWidth: 90, // tile-blank-front 寬（meld-top front kind）
    meldTileHeight: 144, // tile-blank-front 高
    meldGroupGap: 40,
    meldKongStackDirection: MeldKongStackDirection.Up, // 凸字朝玩家
    meldKongStackOffset: 16, // 凸字小幅突出（不疊整張，視覺較緊湊）
    alpha: 1, // 上線值 1；微調對齊時可調 0.5 看重疊
    // 花牌：比照露牌大小（meldScale 0.5）、橫排；手牌「左前方」（視角左=螢幕右、前=螢幕下）
    flowerOriginX: 1130,
    flowerOriginY: 165,
    flowerTileGap: 90, // 同 meldTileGap
    flowerScale: 0.5, // 同 meldScale
    flowerGrowDirection: FlowerGrowDirection.Left, // 第一張在視角左＝螢幕右，往螢幕左排
  },
  // 左家（left）：坐在螢幕左側、面朝右。視角左 = 螢幕上、視角右 = 螢幕下
  3: {
    position: 'left',
    handTopEdgeY: 24, // 手牌「最上邊緣」Y（玩家視角左 = 螢幕上）
    handCenterX: 50, // 手牌中心 X（接近螢幕左）
    handTileGap: 38,
    handScale: 0.8,
    handTileHeight: 99, // tile-side-up 實際高（hand-left 單層側面）
    meldDistanceFromScreenBottom: 130, // 副露「最下邊緣」距畫面底（玩家視角右 = 螢幕下）
    meldCenterX: 60, // 副露中心 X（手牌右側一點）
    meldTileGap: 40,
    meldScale: 0.8,
    meldTileWidth: 90, // tile-side-down 寬
    meldTileHeight: 63, // tile-side-down 高
    meldGroupGap: 40,
    meldKongStackDirection: MeldKongStackDirection.Up, // 凸字朝玩家
    meldKongStackOffset: 20, // 凸字小幅突出（不疊整張，視覺較緊湊）
    alpha: 1, // 上線值 1；微調對齊時可調 0.5 看重疊
    // 花牌：比照露牌大小（meldScale 0.8）、直排；手牌「左前方」（視角左=螢幕上、前=螢幕右）
    flowerOriginX: 150,
    flowerOriginY: 155,
    flowerTileGap: 40, // 同 meldTileGap
    flowerScale: 0.8, // 同 meldScale
    flowerGrowDirection: FlowerGrowDirection.Down, // 第一張在視角左＝螢幕上，往螢幕下排
  },
};

// ============================================================================
// createHandViewForSeat — SEAT_LAYOUT → CHandView config 的對譯層
// ============================================================================
/**
 * 依座位把 SEAT_LAYOUT 的 per-seat 欄位翻譯成 CHandView 的通用 config。
 *
 * SEAT_LAYOUT 用「玩家視角」描述座標（handLeftEdgeX、handDistanceFromScreenBottom 等），
 * 而 CHandView 內部用「螢幕方向」描述（concealedLeftEdgeX、concealedBottomEdgeY 等）。
 * 這個函式就是兩者之間的對譯層。
 *
 * @param scene 主場景
 * @param seat 要建立的座位
 * @param playerSeat 玩家自己的座位（用來判斷 isSelf — 只有自家綁 onTileClick）
 * @param onPlayerTileClick 自家手牌點擊 callback（其他家不會被呼叫）
 */
export function createHandViewForSeat(
  scene: Phaser.Scene,
  seat: TSeat,
  playerSeat: TSeat,
  onPlayerTileClick?: (tile: TTile) => void
): CHandView {
  const layout = SEAT_LAYOUT[seat];
  const isSelf = seat === playerSeat;
  const onTileClick = isSelf && onPlayerTileClick ? { onTileClick: onPlayerTileClick } : {};

  // 副露 common + 視覺 alpha（四家共用）
  const meldCommon = {
    meldTileGap: layout.meldTileGap,
    meldScale: layout.meldScale,
    meldTileWidth: layout.meldTileWidth,
    meldTileHeight: layout.meldTileHeight,
    meldGroupGap: layout.meldGroupGap,
    meldKongStackDirection: layout.meldKongStackDirection,
    ...(layout.meldKongStackOffset !== undefined
      ? { meldKongStackOffset: layout.meldKongStackOffset }
      : {}),
    alpha: layout.alpha,
  };

  // 花牌區（四家共用欄位；origin + direction 簡易排版）
  const flowerCommon = {
    flowerOriginX: layout.flowerOriginX,
    flowerOriginY: layout.flowerOriginY,
    flowerTileGap: layout.flowerTileGap,
    flowerScale: layout.flowerScale,
    flowerGrowDirection: layout.flowerGrowDirection,
    ...(layout.flowerFirstOnTop !== undefined ? { flowerFirstOnTop: layout.flowerFirstOnTop } : {}),
  };

  switch (layout.position) {
    case 'bottom':
      // 自家：手牌橫排 left-anchored、副露橫排 right-anchored
      return new CHandView(scene, {
        position: 'bottom',
        isSelf,
        originX: 0, // 不使用（已由 concealedLeftEdgeX 接管）
        originY: layout.handCenterY,
        tileGap: layout.handTileGap,
        scale: layout.handScale,
        concealedLeftEdgeX: layout.handLeftEdgeX,
        ...(layout.handTileWidth !== undefined ? { concealedTileWidth: layout.handTileWidth } : {}),
        meldOriginY: layout.meldCenterY,
        meldDirection: MeldDirection.Horizontal,
        meldRightEdgeX: LOGICAL_WIDTH - layout.meldDistanceFromScreenRight,
        ...meldCommon,
        ...flowerCommon,
        ...onTileClick,
      });
    case 'right':
      // 右家：手牌直排 bottom-anchored、副露直排 top-anchored
      return new CHandView(scene, {
        position: 'right',
        isSelf,
        originX: layout.handCenterX,
        originY: 0, // 不使用
        tileGap: layout.handTileGap,
        scale: layout.handScale,
        concealedBottomEdgeY: LOGICAL_HEIGHT - layout.handDistanceFromScreenBottom,
        ...(layout.handTileHeight !== undefined
          ? { concealedTileHeight: layout.handTileHeight }
          : {}),
        meldOriginX: layout.meldCenterX,
        meldDirection: MeldDirection.Vertical,
        meldTopEdgeY: layout.meldTopEdgeY,
        ...meldCommon,
        ...flowerCommon,
        ...onTileClick,
      });
    case 'top':
      // 對家：手牌橫排 right-anchored、副露橫排 left-anchored
      return new CHandView(scene, {
        position: 'top',
        isSelf,
        originX: 0,
        originY: layout.handCenterY,
        tileGap: layout.handTileGap,
        scale: layout.handScale,
        concealedRightEdgeX: LOGICAL_WIDTH - layout.handDistanceFromScreenRight,
        ...(layout.handTileWidth !== undefined ? { concealedTileWidth: layout.handTileWidth } : {}),
        meldOriginY: layout.meldCenterY,
        meldDirection: MeldDirection.Horizontal,
        meldLeftEdgeX: layout.meldLeftEdgeX,
        ...meldCommon,
        ...flowerCommon,
        ...onTileClick,
      });
    case 'left':
      // 左家：手牌直排 top-anchored、副露直排 bottom-anchored
      return new CHandView(scene, {
        position: 'left',
        isSelf,
        originX: layout.handCenterX,
        originY: 0,
        tileGap: layout.handTileGap,
        scale: layout.handScale,
        concealedTopEdgeY: layout.handTopEdgeY,
        ...(layout.handTileHeight !== undefined
          ? { concealedTileHeight: layout.handTileHeight }
          : {}),
        meldOriginX: layout.meldCenterX,
        meldDirection: MeldDirection.Vertical,
        meldBottomEdgeY: LOGICAL_HEIGHT - layout.meldDistanceFromScreenBottom,
        ...meldCommon,
        ...flowerCommon,
        ...onTileClick,
      });
  }
}
